import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCommonApi } from '../../api';
import { PATHS } from '../../paths';
import { formatPrice } from '../../utils/formatPrice';
import { DealerOnly, dashboardCardSx } from '../common';
import { useDashboardRange } from './DashboardRangeContext';
import { useRangeDealerStatistics } from './useRecentDealerStatistics';

interface MerchantStatisticWidgetProps {
  numberOfAccidentThisMonth: number;
  commissionThisMonth: string;
  onShowAll?: () => void;
  estimatedPayment?: number | null;
  nextCommissionRemaining?: number | null;
  periodLabel: string;
  payoutLabel: string;
  isLoading?: boolean;
}

interface MetricProps {
  label: string;
  value: ReactNode;
  isLoading?: boolean;
}

const Metric = ({ label, value, isLoading }: MetricProps) => (
  <Stack className="stat-metric" spacing={1} justifyContent="space-between" sx={{ minWidth: 0 }}>
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
      {label}
    </Typography>
    {isLoading ? (
      <Skeleton variant="rounded" width={72} height={40} />
    ) : (
      <Typography
        sx={{
          fontSize: { xs: '2rem', md: '2.25rem' },
          fontWeight: 600,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {value}
      </Typography>
    )}
  </Stack>
);

const MerchantStatisticWidget = ({
  numberOfAccidentThisMonth,
  commissionThisMonth,
  onShowAll,
  estimatedPayment,
  nextCommissionRemaining,
  periodLabel,
  payoutLabel,
  isLoading
}: MerchantStatisticWidgetProps) => (
  <Box
    sx={{ ...dashboardCardSx, height: 1, display: 'flex', flexDirection: 'column', padding: { xs: 2, md: 3 }, gap: 3 }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
      <Stack>
        <Typography variant="h6" fontWeight={600}>
          Moje statistiky
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {periodLabel}
        </Typography>
      </Stack>
      {onShowAll && (
        <Button
          color="primary"
          endIcon={<ArrowForwardIcon />}
          onClick={onShowAll}
          sx={{ borderRadius: 2, flexShrink: 0 }}
        >
          Všechna období
        </Button>
      )}
    </Stack>
    <Box
      sx={{
        display: 'grid',
        gap: { xs: 3, lg: 0 },
        gridTemplateColumns: { xs: '1fr 1fr', lg: '1.5fr 1fr 1fr 1fr' },
        flexGrow: 1,
        alignItems: 'stretch',
        '& > .stat-metric': {
          paddingInline: { lg: 3 },
          borderLeft: { lg: '1px solid' },
          borderColor: { lg: 'rgba(34, 34, 52, 0.08)' }
        }
      }}
    >
      <Box
        sx={{
          gridColumn: { xs: '1 / -1', lg: 'auto' },
          borderRadius: 2,
          backgroundColor: 'rgba(198, 35, 39, 0.06)',
          padding: 3,
          marginInlineEnd: { lg: 3 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 1
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {payoutLabel}
        </Typography>
        {isLoading ? (
          <Skeleton variant="rounded" width={180} height={48} />
        ) : (
          <Typography
            color="primary"
            sx={{
              fontSize: { xs: '2.25rem', md: '2.75rem' },
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            {estimatedPayment != null ? formatPrice(estimatedPayment) : '-'}
          </Typography>
        )}
      </Box>
      <Metric label="Řešené nehody s autopůjčovnou" value={numberOfAccidentThisMonth} isLoading={isLoading} />
      <Metric label="Výše provize" value={commissionThisMonth} isLoading={isLoading} />
      <Metric label="Nehod do vyšší provize" value={nextCommissionRemaining ?? '-'} isLoading={isLoading} />
    </Box>
  </Box>
);

const MerchantStatisticWidgetConnectedContent = () => {
  const navigate = useNavigate();
  const api = useCommonApi();
  const { month, isCurrentMonth, label } = useDashboardRange();
  const { counts, isLoading: isRangeLoading } = useRangeDealerStatistics();

  // Current month: live values from the dashboard endpoints.
  const { data: dashboardStatisticsData, isLoading: isStatsLoading } = useQuery({
    ...api.getDashboardStatistics(),
    enabled: isCurrentMonth
  });
  const { data: estimatedPayment, isLoading: isPaymentLoading } = useQuery({
    ...api.getEstimatedPayments(),
    enabled: isCurrentMonth
  });

  // Other single month: monthly reward row (count, commission %, payout).
  const rewardsYear = month ? String(month.getFullYear()) : '';
  const { data: rewards, isLoading: isRewardsLoading } = useQuery({
    ...api.getDealerRewards(rewardsYear),
    enabled: Boolean(month) && !isCurrentMonth
  });
  const reward = month
    ? rewards?.byDealers?.monthlyRewards?.find((item) => item.monthNumber === month.getMonth() + 1)
    : undefined;

  const rangeAccepted = counts.reduce((sum, c) => sum + c.accepted, 0);

  if (isCurrentMonth) {
    return (
      <MerchantStatisticWidget
        isLoading={isStatsLoading || isPaymentLoading}
        periodLabel={label}
        payoutLabel="Odhadovaná výplata"
        numberOfAccidentThisMonth={dashboardStatisticsData?.countAccidentsAccepted ?? 0}
        commissionThisMonth={`${dashboardStatisticsData?.totalCommission ?? 0} %`}
        onShowAll={() => navigate(PATHS.STATISTICS)}
        estimatedPayment={estimatedPayment?.estimatedPayment ?? 0}
        nextCommissionRemaining={dashboardStatisticsData?.nextCommissionRemaining ?? 0}
      />
    );
  }

  return (
    <MerchantStatisticWidget
      isLoading={month ? isRewardsLoading : isRangeLoading}
      periodLabel={label}
      payoutLabel="Výplata"
      numberOfAccidentThisMonth={month ? reward?.count ?? rangeAccepted : rangeAccepted}
      commissionThisMonth={reward ? `${reward.percentage} %` : '-'}
      onShowAll={() => navigate(PATHS.STATISTICS)}
      estimatedPayment={reward ? reward.commissionValue : null}
      nextCommissionRemaining={null}
    />
  );
};

export const MerchantStatisticWidgetConnected = () => (
  <DealerOnly>
    <MerchantStatisticWidgetConnectedContent />
  </DealerOnly>
);
